// «Цель месяца» — dashboard digest of the deals RevenueGoalBar: current
// won revenue vs the monthly target, an amber hairline progress bar and the
// days left in the month. Click opens /deals. Same numbers as the source
// widget (won sum over the deals list, default target) — no parallel logic.

import { useMemo, type ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { ArrowUpRight, Target } from 'lucide-react';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import { useDeals } from '../../features/deals/hooks/useDeals.js';
import type { DealListItem } from '../../features/deals/api/deals.client.js';

export const REVENUE_GOAL_TARGET = 1_000_000;

export interface RevenueGoalWidgetProps {
  deals?: readonly DealListItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onOpenDeals?: () => void;
}

export function RevenueGoalWidget({
  deals,
  className,
  testId,
  loading = false,
  onOpenDeals,
}: RevenueGoalWidgetProps): ReactElement {
  const { t, tc, locale } = useTranslation();
  // Self-fetching (same pattern as OpenTasks/RecentNotes): the dashboard does
  // not pipe deals through the view-model for this digest.
  const dealsQuery = useDeals();
  const effectiveDeals = deals ?? dealsQuery.deals;
  const effectiveLoading = loading || dealsQuery.isLoading;

  const metrics = useMemo(() => {
    let wonAmount = 0;
    for (const deal of effectiveDeals) {
      if (deal.stage !== 'Won') continue;
      const parsed = typeof deal.amount === 'string' ? Number(deal.amount) : (deal.amount ?? 0);
      wonAmount += Number.isFinite(parsed) ? parsed : 0;
    }
    const progressPercent = Math.min(Math.round((wonAmount / REVENUE_GOAL_TARGET) * 100), 100);
    return { wonAmount, progressPercent };
  }, [effectiveDeals]);

  const daysLeft = useMemo(() => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return Math.max(Math.ceil((endOfMonth.getTime() - now.getTime()) / 86_400_000), 0);
  }, []);

  const numberLocale = locale === 'en' ? 'en-US' : 'ru-RU';
  const formatMoney = (value: number): string =>
    `${new Intl.NumberFormat(numberLocale).format(value)} ₽`;
  const canOpen = typeof onOpenDeals === 'function';

  const widgetContainerProps = {
    title: t('crm.dashboard.revenue_goal.title'),
    icon: Target,
    iconColor: 'var(--ant-color-primary-border-hover, #d97706)',
    minHeight: 140,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        {...(canOpen ? { onClick: onOpenDeals } : {})}
        disabled={!canOpen}
        className="flex items-center gap-1 font-medium"
        style={{
          fontSize: 11,
          color: canOpen
            ? 'var(--ant-color-primary-text, #b45309)'
            : 'var(--ant-color-text-tertiary, #6d6963)',
          cursor: canOpen ? 'pointer' : 'default',
          transition: 'opacity 110ms ease',
        }}
      >
        {t('crm.dashboard.revenue_goal.open')} <ArrowUpRight size={10} />
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {effectiveLoading ? (
        <div
          className="animate-pulse"
          style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <div
            style={{
              height: 28,
              borderRadius: 'var(--ant-border-radius-sm, 6px)',
              background: 'var(--g-fill, #f5f1eb)',
            }}
          />
          <div style={{ height: 6, borderRadius: 3, background: 'var(--g-fill, #f5f1eb)' }} />
        </div>
      ) : (
        <button
          type="button"
          {...(canOpen ? { onClick: onOpenDeals } : {})}
          disabled={!canOpen}
          className="flex w-full flex-col gap-2.5 border-0 bg-transparent px-3.5 py-3 text-left"
          style={{ cursor: canOpen ? 'pointer' : 'default' }}
          data-testid="revenue-goal-body"
        >
          <span className="flex items-baseline gap-2">
            <span
              className="truncate"
              style={{
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'var(--ant-color-text, #292524)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(metrics.wonAmount)}
            </span>
            <span
              className="shrink-0"
              style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary, #6d6963)' }}
            >
              {t('crm.dashboard.revenue_goal.of')} {formatMoney(REVENUE_GOAL_TARGET)}
            </span>
          </span>
          {/* Amber hairline progress (spec D): 1px rail, 6px amber fill. */}
          <span
            role="progressbar"
            aria-valuenow={metrics.progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('crm.dashboard.revenue_goal.progress_label')}
            className="block w-full overflow-hidden"
            style={{
              height: 6,
              borderRadius: 3,
              background: 'var(--ant-color-border-secondary, #e7e5e4)',
            }}
          >
            <span
              className="block"
              style={{
                width: `${metrics.progressPercent}%`,
                height: '100%',
                borderRadius: 3,
                background: 'var(--ant-color-primary-border-hover, #d97706)',
                transition: 'width 300ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
          </span>
          <span className="flex items-center gap-2" style={{ fontSize: 11 }}>
            <span
              style={{
                color: 'var(--ant-color-primary-text, #b45309)',
                fontWeight: 550,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {metrics.progressPercent}%
            </span>
            <span style={{ color: 'var(--ant-color-text-tertiary, #6d6963)' }}>
              {tc('crm.dashboard.revenue_goal.days_left', daysLeft)}
            </span>
          </span>
        </button>
      )}
    </WidgetContainer>
  );
}
